import styled from 'styled-components';
import Select from 'react-select';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px;
`;

export const HeaderSection = styled.div`
  margin-bottom: 24px;

  h2 {
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }

  p {
    color: #666;
    margin: 0;
  }
`;

export const FilterSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  align-items: center;
`;

export const SearchInput = styled.input`
  flex: 1;
  max-width: 300px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

export const AccountFilter = styled(Select)`
  min-width: 200px;
  font-size: 14px;

  .Select__control {
    border-color: #ddd;

    &:hover {
      border-color: #bbb;
    }
  }
`;

export const ClassificationFilter = styled(Select)`
  min-width: 150px;
  font-size: 14px;

  .Select__control {
    border-color: #ddd;

    &:hover {
      border-color: #bbb;
    }
  }
`;

export const ResetButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: #f8f9fa;
  }
`;

export const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #666;
`;

const pulseAnimation = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.7; }
    100% { opacity: 1; }
  }
`;

export const SubmitButton = styled.button<{
  $bgColor?: string;
  $borderColor?: string;
  $isLoading?: boolean;
}>`
  padding: 8px 16px;
  background: ${props => (props.disabled ? '#d6d6d6' : props.$bgColor || '#007bff')};
  border: 1px solid
    ${props => (props.disabled ? '#d6d6d6' : props.$borderColor || props.$bgColor || '#007bff')};
  color: ${props => (props.disabled ? '#999' : 'white')};
  border-radius: 4px;
  font-size: 14px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  transition: all 0.2s ease-in-out;
  transform: none;
  box-shadow: none;
  position: relative;

  ${pulseAnimation}
  ${props => props.$isLoading && `animation: pulse 1.5s ease-in-out infinite;`}

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$bgColor) {
        // Darken the custom color by reducing brightness
        const color = props.$bgColor;
        if (color === '#059669') return '#047857'; // Green
        if (color === '#7c3aed') return '#6d28d9'; // Purple
        if (color === '#2563eb') return '#1d4ed8'; // Blue
        return '#0056b3'; // Default blue
      }
      return '#0056b3';
    }};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const TableSection = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #666;
  font-size: 16px;
`;

export const TransactionTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

export const HeaderRow = styled.tr`
  background: #f8f9fa;
  border-bottom: 2px solid #ddd;
`;

export const HeaderCell = styled.th<{ width?: string; align?: string }>`
  padding: 12px 8px;
  text-align: ${props => props.align || 'left'};
  font-weight: 600;
  color: #333;
  ${props => props.width && `width: ${props.width};`}
`;

export const TransactionRow = styled.tr`
  border-bottom: 1px solid #eee;
  cursor: pointer;

  &:hover {
    background: #f8f9fa;
  }
`;

export const TransactionCell = styled.td<{ align?: string }>`
  padding: 12px 8px;
  text-align: ${props => props.align || 'left'};
  vertical-align: middle;
`;

export const CheckboxCell = styled.td`
  padding: 12px 8px;
  vertical-align: middle;
`;

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  cursor: pointer;
`;

// React-Table specific styles
export const ReactTable = styled.div.attrs({
  role: 'table',
})`
  width: 100%;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  overflow: hidden;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const ReactTableHeader = styled.div.attrs({
  role: 'rowgroup',
})`
  display: table;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  background: #f8f9fa;
  border-bottom: 2px solid #e1e5e9;
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
`;

export const ReactTableHeaderRow = styled.div.attrs({
  role: 'row',
})`
  display: table-row;
`;

export const ReactTableHeaderCell = styled.div.attrs({
  role: 'columnheader',
})<{
  width?: number;
  align?: string;
  canSort?: boolean;
}>`
  display: table-cell;
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  font-weight: 600;
  color: #374151;
  vertical-align: middle;
  white-space: nowrap;
  ${props => props.width && `width: ${props.width}px;`}
  ${props =>
    props.canSort &&
    `
    cursor: pointer;
    user-select: none;
    
    &:hover {
      background-color: #e5e7eb;
    }
  `}
  
  /* Sort indicator */
  &[data-sort='asc']::after {
    content: ' ↑';
    color: #2563eb;
  }

  &[data-sort='desc']::after {
    content: ' ↓';
    color: #2563eb;
  }
`;

export const ReactTableBody = styled.div.attrs({
  role: 'rowgroup',
})`
  width: 100%;
  position: relative;
  flex: 1;
  overflow-y: auto;
`;

const shimmerAnimation = `
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

export const ReactTableRow = styled.div.attrs({
  role: 'row',
})<{ isSelected?: boolean; isPending?: boolean }>`
  display: table;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  border-bottom: 1px solid #e1e5e9;
  cursor: pointer;

  ${shimmerAnimation}

  ${props => {
    if (props.isPending && props.isSelected) {
      return `
        background: linear-gradient(90deg, #e3f2fd 0%, #e3f2fd 40%, rgba(255, 179, 0, 0.2) 50%, #e3f2fd 60%, #e3f2fd 100%);
        background-size: 200% 100%;
        animation: shimmer 2s infinite linear;
      `;
    } else if (props.isPending) {
      return `
        background: linear-gradient(90deg, #f5f5f5 0%, #f5f5f5 40%, rgba(255, 179, 0, 0.3) 50%, #f5f5f5 60%, #f5f5f5 100%);
        background-size: 200% 100%;
        animation: shimmer 2s infinite linear;
      `;
    } else if (props.isSelected) {
      return `background: #e3f2fd;`;
    }
    return '';
  }}
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

export const ReactTableCell = styled.div.attrs({
  role: 'cell',
})<{ align?: string; width?: number }>`
  display: table-cell;
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${props => props.width && `width: ${props.width}px;`}
`;
